import type { ApplicationInsights } from '@microsoft/applicationinsights-web';

import { attachClickTelemetry } from './clicks';
import {
  apiHost,
  appVersion,
  connectionString,
  environment,
  toTelemetryUserId,
} from './config';
import {
  getTelemetryContext,
  setTelemetryContext,
  TelemetryContext,
} from './context';
import { toEndpointTemplate } from './endpoint';
import { isControlFlowRejection, toError } from './errors';
import {
  ClientErrorSource,
  MEASUREMENT_KEYS,
  TelemetryEventName,
  TelemetryEvents,
} from './events';
import { safely } from './safely';
import { addActiveTime, peekSession, resolveSession } from './session';

const MAX_DEFERRED = 200;

const ERROR_NAMES: Record<ClientErrorSource, string> = {
  render: 'RenderError',
  rejection: 'UnhandledRejection',
  window: 'WindowError',
};

let appInsights: ApplicationInsights | undefined;
let deferred: Array<(sink: ApplicationInsights) => void> = [];
let lastVisibleAt = 0;
let sessionEnded = false;
let initialized = false;

// Queues until the lazy SDK import lands.
const withSink = (fn: (sink: ApplicationInsights) => void) => {
  if (appInsights) {
    fn(appInsights);
    return;
  }
  if (connectionString && deferred.length < MAX_DEFERRED) deferred.push(fn);
};

const splitPayload = (properties: object) => {
  const dimensions: Record<string, string> = {};
  const measurements: Record<string, number> = {};

  Object.entries(properties).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (typeof value === 'number' && MEASUREMENT_KEYS.has(key)) {
      measurements[key] = value;
    } else {
      dimensions[key] = String(value);
    }
  });

  return { dimensions, measurements };
};

const buildEnvelope = (
  sessionId: string,
  context: TelemetryContext,
): Record<string, string> => {
  const { userId, userEmail, userName, role, page, modal, orderPid } = context;

  const envelope: Record<string, string> = {
    sessionId,
    appVersion,
    environment,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
  };

  if (page) envelope.page = page;
  if (userId) envelope.userId = userId;
  if (userEmail) envelope.userEmail = userEmail;
  if (userName) envelope.userName = userName;
  if (role) envelope.role = role;
  if (modal) envelope.modal = modal;
  if (orderPid) envelope.orderPid = orderPid;

  return envelope;
};

function emit<K extends TelemetryEventName>(
  name: K,
  properties: TelemetryEvents[K],
  sessionId: string,
  context: TelemetryContext,
) {
  const { dimensions, measurements } = splitPayload(properties);
  const payload = { ...buildEnvelope(sessionId, context), ...dimensions };

  if (!connectionString) {
    // The only signal without --appinsights: nothing leaves the browser.
    // eslint-disable-next-line no-console
    console.debug('[telemetry]', name, payload, measurements);
    return;
  }

  withSink((sink) =>
    sink.trackEvent({ name, properties: payload, measurements }),
  );
}

// Every touch bumps the idle clock; the first touch after 30 idle minutes opens a new session.
const touchSession = (context: TelemetryContext) => {
  const session = resolveSession(Date.now());
  if (session.isNew) {
    sessionEnded = false;
    emit('session_started', {}, session.id, context);
  }
  return session;
};

export function track<K extends TelemetryEventName>(
  name: K,
  properties: TelemetryEvents[K],
  context?: TelemetryContext,
): void {
  safely(() => {
    const resolved = context ?? getTelemetryContext();
    const session = touchSession(resolved);
    emit(name, properties, session.id, resolved);
  });
}

/** The session id for the `x-telemetry-session` header; a request counts as activity. */
export function getSessionId(): string {
  let id = '';
  safely(() => {
    id = touchSession(getTelemetryContext()).id;
  });
  return id;
}

export function trackPageView(name: string, uri: string): void {
  safely(() => withSink((sink) => sink.trackPageView({ name, uri })));
}

export function trackClientError(
  error: unknown,
  details: { source: ClientErrorSource; componentStack?: string },
): void {
  safely(() => {
    const { source, componentStack } = details;
    const asError = toError(error, ERROR_NAMES[source]);
    // The stack goes to `exceptions` only: it can embed customer data.
    track('client_error', { message: asError.message, componentStack, source });
    withSink((sink) =>
      sink.trackException({
        exception: asError,
        properties: { componentStack, source },
      }),
    );
  });
}

export function setTelemetryUser(
  sub: string,
  email?: string,
  name?: string,
): void {
  safely(() => {
    const userId = toTelemetryUserId(sub);
    setTelemetryContext({ userId, userEmail: email, userName: name });
    withSink((sink) =>
      sink.setAuthenticatedUserContext(userId, undefined, true),
    );
    track('user_identified', {});
  });
}

export function clearTelemetryUser(): void {
  safely(() => {
    setTelemetryContext({
      userId: undefined,
      userEmail: undefined,
      userName: undefined,
      role: undefined,
    });
    withSink((sink) => sink.clearAuthenticatedUserContext());
  });
}

const settleActiveTime = () => {
  const now = Date.now();
  if (lastVisibleAt !== 0) addActiveTime(now - lastVisibleAt);
  lastVisibleAt = document.visibilityState === 'visible' ? now : 0;
};

const endSession = () => {
  if (sessionEnded) return;
  settleActiveTime();
  const session = peekSession();
  if (!session) return;
  sessionEnded = true;
  emit(
    'session_ended',
    { durationMs: Date.now() - session.startedAt, activeMs: session.activeMs },
    session.id,
    getTelemetryContext(),
  );
};

const METHOD_AND_URL = /^([A-Z]+) (.+)$/;

const stripQuery = (value: string) =>
  value.includes('?') ? value.slice(0, value.indexOf('?')) : value;

// The SDK names a dependency `GET https://host/api/orders/17?page=1&customerName=…`: the id
// hides the route and the query holds typed filter text. `name` becomes the endpoint template
// (so p95 by endpoint works); `data` and `target` keep the URL but lose the query.
export function templateDependency(item: {
  baseType?: string;
  baseData?: Record<string, unknown>;
}): void {
  if (item.baseType !== 'RemoteDependencyData' || !item.baseData) return;
  const { baseData } = item;

  const { name } = baseData;
  if (typeof name === 'string') {
    const match = METHOD_AND_URL.exec(name);
    baseData.name = match
      ? `${match[1]} ${toEndpointTemplate(match[2])}`
      : toEndpointTemplate(name);
  }
  ['data', 'target'].forEach((field) => {
    const value = baseData[field];
    if (typeof value === 'string') baseData[field] = stripQuery(value);
  });
}

async function loadSink() {
  const { ApplicationInsights: Sdk } = await import(
    '@microsoft/applicationinsights-web'
  );

  const sink = new Sdk({
    config: {
      connectionString,
      // Route changes are reported by useRouteTelemetry on pathname changes only.
      enableAutoRouteTracking: false,
      autoTrackPageVisitTime: false,
      // `traceparent` on API calls only, so browser dependencies join API requests.
      enableCorsCorrelation: true,
      correlationHeaderDomains: apiHost ? [apiHost] : undefined,
      // The SDK's own tracking stringifies the rejection reason, losing message, type and stack.
      enableUnhandledPromiseRejectionTracking: false,
      disableFetchTracking: false,
      // Fonts and other build assets are not API calls; keep them out of the dependencies table.
      excludeRequestFromAutoTrackingPatterns: [/\/static\//],
    },
  });
  sink.loadAppInsights();
  sink.addTelemetryInitializer((item) => {
    const envelope = item;
    const sessionId = peekSession()?.id ?? '';
    const tags: Record<string, unknown> = envelope.tags || {};
    tags['ai.application.ver'] = appVersion;
    envelope.tags = tags as typeof envelope.tags;
    // Envelope first so event properties win.
    envelope.data = {
      ...buildEnvelope(sessionId, getTelemetryContext()),
      ...envelope.data,
    };
    templateDependency(envelope);
  });

  appInsights = sink;
  const queued = deferred;
  deferred = [];
  queued.forEach((run) => safely(() => run(sink)));
}

export function initTelemetry(): void {
  // The listeners below are fresh closures, so a second call would double-report everything.
  if (initialized) return;
  initialized = true;

  safely(() => {
    if (connectionString) {
      // An ad blocker or a purged chunk rejects the import; strand nothing if it does.
      loadSink().catch(() => {
        deferred = [];
      });
    }

    lastVisibleAt = document.visibilityState === 'visible' ? Date.now() : 0;

    window.addEventListener('unhandledrejection', (event) =>
      safely(() => {
        if (isControlFlowRejection(event.reason)) return;
        trackClientError(event.reason, { source: 'rejection' });
      }),
    );

    window.addEventListener('error', (event) =>
      safely(() => {
        // Only uncaught errors reach here without capture; still, never report an empty one.
        if (!event.error && !event.message) return;
        trackClientError(event.error ?? event.message, { source: 'window' });
      }),
    );

    document.addEventListener('visibilitychange', () =>
      safely(() => {
        settleActiveTime();
        if (document.visibilityState === 'hidden') appInsights?.onunloadFlush();
      }),
    );

    window.addEventListener('pagehide', () =>
      safely(() => {
        endSession();
        // onunloadFlush uses beacon; flush() is a sync XHR browsers block on unload.
        appInsights?.onunloadFlush();
      }),
    );

    // A bfcache restore resumes the same session, so it has to be closable again.
    window.addEventListener('pageshow', (event) =>
      safely(() => {
        if (!event.persisted) return;
        sessionEnded = false;
        lastVisibleAt = document.visibilityState === 'visible' ? Date.now() : 0;
      }),
    );

    attachClickTelemetry(document);
    touchSession(getTelemetryContext());
  });
}
